import {useMutation} from '@tanstack/react-query';
import {useCallback} from 'react';
import {useAssistantIntegration} from '../AssistantIntegration';
import {handleResponse} from './http';
import {AttachmentInfo, CreateRunBody, StartRunResult} from './types';

export interface CreateRunArgs {
    threadId: string;
    body: CreateRunBody;
    /**
     * Awaited before POSTing so the SignalR group is joined and no events are missed
     * between the request and the first streamed token.
     */
    waitUntilJoined?: () => Promise<void>;
}

export function useCreateRun() {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    const mutation = useMutation({
        mutationFn: async ({
            threadId,
            body,
            waitUntilJoined
        }: CreateRunArgs) => {
            if (waitUntilJoined) {
                await waitUntilJoined();
            }

            const response = await authenticatedFetch(
                `${baseUrl}/api/assistant-threads/${threadId}/runs`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        ...body,
                        timeZoneId:
                            body.timeZoneId ??
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                    })
                }
            );
            return handleResponse<StartRunResult>(response);
        }
    });

    const cancelRun = useCallback(
        async (threadId: string, runId: string) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistant-threads/${threadId}/runs/${runId}/cancel`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: '{}'
                }
            );
            return handleResponse<boolean>(response);
        },
        [authenticatedFetch, baseUrl]
    );

    return {...mutation, cancelRun};
}

export function useUploadAttachment() {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);

            const response = await authenticatedFetch(
                `${baseUrl}/api/assistant-attachments`,
                {method: 'POST', body: formData}
            );
            return handleResponse<AttachmentInfo>(response);
        }
    });
}
